import React, { useState, useEffect, useCallback } from "react";
import {
  Home,
  Users,
  ClipboardList,
  LogOut,
  Building2,
  Megaphone,
  BarChart2,
  User,
  Menu,
  Download,
  Filter,
  CheckCircle,
  XCircle,
  UsersRound,
  FileCheck,
  FileSignature,
  DoorOpen,
  Receipt,
  FileText,
  CreditCard,
  Send,
  Wrench,
  Eye,
  Clock,
  AlertTriangle,
  TrendingUp,
  Flame,
  TrendingDown,
  Zap,
  Droplets,
  Thermometer,
  Bug,
  Brush,
  Shield,
  AlertCircle,
  Loader,
  X,
  Calendar,
  Phone,
  SortAsc,
  SortDesc,
  Search,
  LifeBuoy,
} from "lucide-react";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import Manager from "../ManagerBio/Manager";
import PopupAssign from "../PopupAssign/PopupAssign";
import ProfileStudentAdm from "../ProfileStudentAdm/ProfileStudentAdm";
import MSASupportPage from "../MSASupport/MSASupportPage";
import {
  FaSwimmingPool,
  FaGamepad,
  FaWifi,
  FaUtensils,
  FaLock,
  FaCoffee,
  FaChair,
  FaFutbol
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useNavigate, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

const ResidenceAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("stats");
  const [residenceStats, setResidenceStats] = useState({});
  const [studentsList, setStudentsList] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [showAssignPopup, setShowAssignPopup] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Maintenance states
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [filteredMaintenance, setFilteredMaintenance] = useState([]);
  const [maintenanceSearch, setMaintenanceSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [dateSortOrder, setDateSortOrder] = useState("desc");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [maintenanceStats, setMaintenanceStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0
  });

  const navigate = useNavigate();
  const location = useLocation();
  const managerData = JSON.parse(sessionStorage.getItem("residenceManager"));

  const [currentPage, setCurrentPage] = useState(1);
  const STUDENTS_PER_PAGE = 15;
  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / STUDENTS_PER_PAGE));
  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * STUDENTS_PER_PAGE,
    currentPage * STUDENTS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredStudents.length]);

  useEffect(() => {
    if (!managerData) navigate("/ResidenceLogin");
  }, [managerData, navigate]);

  const [showBillingFilter, setShowBillingFilter] = useState(false);
  const [billingFilter, setBillingFilter] = useState({
    startMonth: "",
    endMonth: "",
    status: "all",
  });

  const residenceId = location.state?.residenceId || managerData?.residence?.residenceId;
  const residenceName = location.state?.residenceName || managerData?.residence?.name;

  const [announcement, setAnnouncement] = useState({
    title: "",
    content: "",
    audience: "all",
    attachments: [],
  });

  // Fetch maintenance requests
  const fetchMaintenanceRequests = async () => {
    if (!residenceId) return;
    try {
      const response = await axios.get(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Maintenance/residence/${residenceId}`);
      setMaintenanceRequests(response.data);
      applyFilters(response.data, statusFilter, priorityFilter, maintenanceSearch, dateSortOrder);

      const stats = {
        total: response.data.length,
        pending: response.data.filter(r => r.status === "Pending").length,
        inProgress: response.data.filter(r => r.status === "In Progress").length,
        completed: response.data.filter(r => r.status === "Completed").length
      };
      setMaintenanceStats(stats);
    } catch (err) {
      console.error("Error fetching maintenance requests:", err);
      toast.error("Failed to load maintenance requests");
    }
  };

  const applyFilters = (data, status, priority, search, sortOrder) => {
    let filtered = [...data];

    if (status !== "all") {
      filtered = filtered.filter(r => r.status === status);
    }

    if (priority !== "all") {
      filtered = filtered.filter(r => r.priority === priority);
    }

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(r =>
        (r.studentName || "").toLowerCase().includes(searchLower) ||
        (r.studentPhone || "").toLowerCase().includes(searchLower) ||
        (r.roomNumber || "").toLowerCase().includes(searchLower) ||
        (r.category || "").toLowerCase().includes(searchLower) ||
        (r.description || "").toLowerCase().includes(searchLower)
      );
    }
    

    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
    });

    setFilteredMaintenance(filtered);
  };

  const updateRequestStatus = async (requestId, newStatus, adminNoteText = "") => {
    setUpdatingStatus(true);
    try {
      await axios.put(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Maintenance/${requestId}/status`, {
        status: newStatus,
        adminNotes: adminNoteText
      });
      toast.success(`Request status updated to ${newStatus}`);
      fetchMaintenanceRequests();
      setShowRequestModal(false);
      setAdminNote("");
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error("Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    applyFilters(maintenanceRequests, status, priorityFilter, maintenanceSearch, dateSortOrder);
  };

  const handlePriorityFilter = (priority) => {
    setPriorityFilter(priority);
    applyFilters(maintenanceRequests, statusFilter, priority, maintenanceSearch, dateSortOrder);
  };

  const handleSearchChange = (search) => {
    setMaintenanceSearch(search);
    applyFilters(maintenanceRequests, statusFilter, priorityFilter, search, dateSortOrder);
  };

  const handleDateSortToggle = () => {
    const newOrder = dateSortOrder === "desc" ? "asc" : "desc";
    setDateSortOrder(newOrder);
    applyFilters(maintenanceRequests, statusFilter, priorityFilter, maintenanceSearch, newOrder);
  };

  useEffect(() => {
    if (residenceId) {
      fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/residence/${residenceId}/stats`)
        .then((res) => res.json())
        .then((data) => setResidenceStats(data))
        .catch((err) => console.error("Error fetching stats:", err));

      // ✅ Also load data for the stats charts on initial load
      fetchStudentsWithRoomStatus();
      fetchMaintenanceRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residenceId]);

  const fetchStudentsWithRoomStatus = async () => {
    if (!residenceId) return;
    try {
      const resStudents = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Residence/residence/${residenceId}/students`);
      const students = await resStudents.json();
      const resStatuses = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/RoomStatus/residence/${residenceId}`);
      const statuses = await resStatuses.json();
      const merged = students.map((student) => {
        const status = statuses.find((s) => s.userId === student.id);
        return {
          ...student,
          roomAssigned: status ? status.isAssigned === 1 : false,
          room: status?.room || null,
        };
      });
      setStudentsList(merged);
      setFilteredStudents(merged);
    } catch (err) {
      console.error("Error fetching students with room status:", err);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = studentsList.filter(
      (student) =>
        (student.name || "").toLowerCase().includes(query) ||
        (student.email || "").toLowerCase().includes(query) ||
        (student.studentNumber || "").toLowerCase().includes(query) ||
        (student.phone || "").toLowerCase().includes(query)
    );
    setFilteredStudents(filtered);
  };

  const handleFilterLatest = () => {
    const sorted = [...filteredStudents].sort(
      (a, b) => new Date(b.registerDate) - new Date(a.registerDate)
    );
    setFilteredStudents(sorted);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("residenceManager");
    navigate("/ResidenceLogin");
  };

  const handleViewProfile = (studentId) => {
    setSelectedStudentId(studentId);
    setActiveTab("studentProfile");
  };

  const handleAssignClick = (student) => {
    setSelectedStudent(student);
    setShowAssignPopup(true);
  };

  const handleDownloadReport = () => {
    const data = paginatedStudents.map((student) => ({
      Name: student.name,
      Email: student.email,
      Phone: student.phone,
      Registered: new Date(student.registerDate).toLocaleDateString(),
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${residenceName || "Residence"}_Students_Report.xlsx`);
  };

  const downloadAsExcel = (data, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `${fileName}.xlsx`);
  };

  const sanitizeFileName = (name) =>
    name.trim().replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "");

  const downloadStudentsInRooms = async () => {
    const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/room/${residenceId}/students-in-rooms`);
    const data = await res.json();
    const safeResidenceName = sanitizeFileName(residenceName || "Residence");
    const date = new Date().toISOString().split("T")[0];
    downloadAsExcel(data, `${safeResidenceName}_Students_In_Rooms_${date}`);
  };

  const downloadStudentsBilling = async () => {
    const toastId = toast.loading("Fetching billing data...");
    try {
      const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/billing/residence/${residenceId}`);
      if (!res.ok) throw new Error("Failed to fetch billing");
      const result = await res.json();
      if (!result.students || result.students.length === 0) {
        toast.error("No billing data found", { id: toastId });
        return;
      }
      const rows = [];
      result.students.forEach((account) => {
        account.monthlyBillings?.forEach((month) => {
          rows.push({
            FirstName: account.student?.firstName || "N/A",
            LastName: account.student?.lastName || "N/A",
            StudentNumber: account.student?.studentNumber || "N/A",
            IdNumber: account.student?.idNumber || "N/A",
            Year: month.year,
            Month: month.month,
            MonthlyAmount: month.monthlyAmount,
            AmountPaid: month.amountPaid,
            Status: month.status,
            ExpectedAmount: account.expectedAmount,
            TotalPaid: account.totalPaid,
            OutstandingAmount: account.outstandingAmount,
          });
        });
      });
      const safeResidenceName = sanitizeFileName(residenceName || "Residence");
      const date = new Date().toISOString().split("T")[0];
      downloadAsExcel(rows, `${safeResidenceName}_Billing_Students_Data_${date}`);
      toast.success("Billing report downloaded!", { id: toastId });
    } catch (error) {
      console.error(error);
      toast.error("Error downloading billing report", { id: toastId });
    }
  };

  const downloadFilteredBilling = async () => {
    const toastId = toast.loading("Fetching filtered billing data...");
    try {
      const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/billing/residence/${residenceId}`);
      if (!res.ok) throw new Error("Failed to fetch billing");
      const result = await res.json();
      if (!result.students || result.students.length === 0) {
        toast.error("No billing data available", { id: toastId });
        return;
      }
      const { startMonth, endMonth, status } = billingFilter;
      const startYear = startMonth ? parseInt(startMonth.split("-")[0]) : null;
      const startMon = startMonth ? parseInt(startMonth.split("-")[1]) : null;
      const endYear = endMonth ? parseInt(endMonth.split("-")[0]) : null;
      const endMon = endMonth ? parseInt(endMonth.split("-")[1]) : null;
      const rows = [];
      result.students.forEach((account) => {
        account.monthlyBillings?.forEach((month) => {
          const monthInRange =
            (!startYear || month.year > startYear || (month.year === startYear && month.month >= startMon)) &&
            (!endYear || month.year < endYear || (month.year === endYear && month.month <= endMon));
          const statusMatch = status === "all" || month.status?.toLowerCase() === status.toLowerCase();
          if (monthInRange && statusMatch) {
            rows.push({
              FirstName: account.student?.firstName || "N/A",
              LastName: account.student?.lastName || "N/A",
              StudentNumber: account.student?.studentNumber || "N/A",
              IdNumber: account.student?.idNumber || "N/A",
              Year: month.year,
              Month: month.month,
              MonthlyAmount: month.monthlyAmount,
              AmountPaid: month.amountPaid,
              Status: month.status,
              ExpectedAmount: account.expectedAmount,
              TotalPaid: account.totalPaid,
              OutstandingAmount: account.outstandingAmount,
            });
          }
        });
      });
      if (rows.length === 0) {
        toast.error("No records found for selected filters", { id: toastId });
        return;
      }
      const fileName = `Filtered_Billing_${new Date().toISOString().split("T")[0]}`;
      downloadAsExcel(rows, fileName);
      toast.success("Filtered report downloaded!", { id: toastId });
      setShowBillingFilter(false);
    } catch (error) {
      console.error(error);
      toast.error("Error exporting filtered report", { id: toastId });
    }
  };

  const downloadCompletedProfiles = async () => {
    const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/room/${residenceId}/students-with-complete-profile`);
    const data = await res.json();
    const safeResidenceName = sanitizeFileName(residenceName || "Residence");
    const date = new Date().toISOString().split("T")[0];
    downloadAsExcel(data, `${safeResidenceName}_Completed_Profiles_${date}`);
  };

  const downloadUnsignedLeases = async () => {
    const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/room/${residenceId}/unsigned-leases`);
    const data = await res.json();
    const safeResidenceName = sanitizeFileName(residenceName || "Residence");
    const date = new Date().toISOString().split("T")[0];
    downloadAsExcel(data, `${safeResidenceName}_Unsigned_Leases_${date}`);
  };

  const downloadEmptyRooms = async () => {
    const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/reports/residence/${residenceId}/empty-rooms`);
    const data = await res.json();
    downloadAsExcel(data, "Empty_Rooms");
  };

  const downloadInvoices = async () => {
    const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/invoice/bulk/${residenceId}`);
    if (!res.ok) return;
    const blob = await res.blob();
    const safeResidenceName = sanitizeFileName(residenceName || "Residence");
    const date = new Date().toISOString().split("T")[0];
    const fileName = `${safeResidenceName}_Invoices_${date}.zip`;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadLeases = async () => {
    const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/room/leases/bulk/${residenceId}`);
    if (!res.ok) return;
    const blob = await res.blob();
    const safeResidenceName = sanitizeFileName(residenceName || "Residence");
    const date = new Date().toISOString().split("T")[0];
    const fileName = `${safeResidenceName}_Students_Leases_${date}.zip`;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handlePublishAnnouncement = async () => {
    if (!announcement.title || !announcement.content) {
      toast.error("Please complete the announcement title and content");
      return;
    }
    setPublishing(true);
    try {
      const formData = new FormData();
      formData.append("title", announcement.title);
      formData.append("content", announcement.content);
      formData.append("audience", announcement.audience);
      formData.append("residenceId", residenceId);
      formData.append("createdBy", managerData?.userId);
      announcement.attachments.forEach((file) => {
        formData.append("attachments", file);
      });
      const response = await fetch("https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/announcements", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) throw new Error("Failed to publish announcement");
      toast.success("Announcement published and emails sent successfully");
      setAnnouncement({ title: "", content: "", audience: "all", attachments: [] });
      fetchAnnouncements();
    } catch (error) {
      console.error(error);
      toast.error("Failed to publish announcement");
    } finally {
      setPublishing(false);
    }
  };

  const fetchAnnouncements = useCallback(async () => {
    if (!residenceId) return;
    try {
      const res = await fetch(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/announcements/residence/${residenceId}`);
      const data = await res.json();
      setAnnouncements(data);
    } catch (err) {
      console.error("Failed to load announcements", err);
    }
  }, [residenceId]);

  useEffect(() => {
    if (activeTab === "announcements") {
      fetchAnnouncements();
    } else if (activeTab === "maintenance") {
      fetchMaintenanceRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fetchAnnouncements]);

  const handleDeleteAnnouncement = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete this announcement?");
    if (!confirmed) return;
    try {
      await axios.delete(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/announcements/${id}`);
      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
      toast.success("Announcement deleted");
    } catch (error) {
      console.error("Delete failed", error);
      toast.error("Failed to delete announcement");
    }
  };

  const navItems = [
    { id: "stats", label: "Residence Stats", icon: <BarChart2 className="w-5 h-5" /> },
    { id: "students", label: "Students List", icon: <Users className="w-5 h-5" /> },
    { id: "maintenance", label: "Maintenance", icon: <Wrench className="w-5 h-5" /> },
    { id: "announcements", label: "Announcements", icon: <Megaphone className="w-5 h-5" /> },
    { id: "msaSupport", label: "MSA Support", icon: <LifeBuoy className="w-5 h-5" /> },
    { id: "manager", label: "Residence Manager", icon: <User className="w-5 h-5" /> },
  ];

  const getAmenityIcon = (amenity) => {
    const icons = {
      "Swimming Pool": <FaSwimmingPool />,
      "Game Area": <FaGamepad />,
      "Wi-Fi": <FaWifi />,
      "Kitchen": <FaUtensils />,
      "24/7 Security": <FaLock />,
      "Cafeteria": <FaCoffee />,
      "Chilling Area": <FaChair />,
      "Sports Ground": <FaFutbol />
    };
    return icons[amenity] || null;
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "Low": return <TrendingDown className="w-4 h-4" />;
      case "Medium": return <AlertTriangle className="w-4 h-4" />;
      case "High": return <TrendingUp className="w-4 h-4" />;
      case "Emergency": return <Flame className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Low": return "bg-green-100 text-green-700";
      case "Medium": return "bg-yellow-100 text-yellow-700";
      case "High": return "bg-orange-100 text-orange-700";
      case "Emergency": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending": return "bg-yellow-100 text-yellow-700";
      case "In Progress": return "bg-blue-100 text-blue-700";
      case "Completed": return "bg-green-100 text-green-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      "Plumbing": <Droplets className="w-4 h-4" />,
      "Electrical": <Zap className="w-4 h-4" />,
      "Furniture": <Home className="w-4 h-4" />,
      "Appliance": <Wrench className="w-4 h-4" />,
      "Heating/Cooling": <Thermometer className="w-4 h-4" />,
      "Pest Control": <Bug className="w-4 h-4" />,
      "Cleaning": <Brush className="w-4 h-4" />,
      "Security": <Shield className="w-4 h-4" />,
      "Other": <AlertCircle className="w-4 h-4" />
    };
    return icons[category] || <AlertCircle className="w-4 h-4" />;
  };

  const reports = [
    { title: "Students in Rooms", desc: "All students currently assigned to rooms", icon: <UsersRound className="w-5 h-5" />, action: downloadStudentsInRooms, color: "blue" },
    { title: "Completed Profiles", desc: "Students with no outstanding information", icon: <FileCheck className="w-5 h-5" />, action: downloadCompletedProfiles, color: "green" },
    { title: "Unsigned Leases", desc: "Students in rooms without signed leases", icon: <FileSignature className="w-5 h-5" />, action: downloadUnsignedLeases, color: "amber" },
    { title: "Empty Rooms", desc: "Rooms currently not occupied", icon: <DoorOpen className="w-5 h-5" />, action: downloadEmptyRooms, color: "purple" },
    { title: "Invoices", desc: "All invoices (bulk export)", icon: <Receipt className="w-5 h-5" />, action: downloadInvoices, color: "rose" },
    { title: "Lease Agreements", desc: "All lease agreements", icon: <FileText className="w-5 h-5" />, action: downloadLeases, color: "indigo" },
    { title: "Student Billing Report", desc: "All Students Billing Information", icon: <CreditCard className="w-5 h-5" />, action: downloadStudentsBilling, hasFilter: true, color: "teal" },
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: "bg-blue-50", text: "text-blue-600", border: "hover:border-blue-200" },
      green: { bg: "bg-green-50", text: "text-green-600", border: "hover:border-green-200" },
      amber: { bg: "bg-amber-50", text: "text-amber-600", border: "hover:border-amber-200" },
      purple: { bg: "bg-purple-50", text: "text-purple-600", border: "hover:border-purple-200" },
      rose: { bg: "bg-rose-50", text: "text-rose-600", border: "hover:border-rose-200" },
      indigo: { bg: "bg-indigo-50", text: "text-indigo-600", border: "hover:border-indigo-200" },
      teal: { bg: "bg-teal-50", text: "text-teal-600", border: "hover:border-teal-200" },
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-md z-40 px-4 py-3 flex items-center justify-between border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
            <Building2 className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-gray-800">Admin Panel</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg hover:bg-gray-100 transition">
          <Menu className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-72 bg-gray-900 shadow-xl z-30 transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="px-6 py-8 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-white ring-2 ring-white/15 flex-shrink-0 p-1">
                <img
                  src="/LOGOSTAY.png"
                  alt="MSA Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Panel</h1>
                <p className="text-xs text-gray-500">Residence Management</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  if (item.id === "students") fetchStudentsWithRoomStatus();
                  if (item.id === "maintenance") fetchMaintenanceRequests();
                  setMobileMenuOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${activeTab === item.id
                  ? "bg-gray-800 text-white"
                  : "text-gray-400 hover:bg-gray-800 hover:text-gray-200"
                  }`}
              >
                <span>{item.icon}</span>
                <span className="text-sm font-medium">{item.label}</span>
                {activeTab === item.id && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500" />}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-800">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-700 text-gray-400 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-200"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
            <p className="text-[10px] text-gray-600 text-center mt-4">
              Powered by Basic Intelligent Software (BIS)
            </p>
          </div>
        </div>
      </aside>
      {/* Main Content */}
      <main className="lg:ml-72">
        <div className="p-5 md:p-8 pt-20 lg:pt-8">
          {/* Header Banner */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gray-100 rounded-xl">
                  <Building2 className="w-6 h-6 text-gray-700" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{residenceName}</h1>
                  <p className="text-sm text-gray-500 mt-0.5">Residence Administration Panel</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Logged in as</p>
                <p className="text-sm font-semibold text-gray-700">Residence Administrator</p>
              </div>
            </div>
          </div>

          {/* Stats Tab */}
          {activeTab === "stats" && (
            <div className="space-y-6">
              {/* Metric Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 shadow-lg shadow-blue-200 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Home className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-sm text-blue-100">Occupancy</p>
                  <p className="text-2xl font-bold mt-1">{residenceStats.occupancy || 0}</p>
                </div>

                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 shadow-lg shadow-emerald-200 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-sm text-emerald-100">Capacity</p>
                  <p className="text-2xl font-bold mt-1">{residenceStats.capacity || 0}</p>
                </div>

                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 shadow-lg shadow-purple-200 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <BarChart2 className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-sm text-purple-100">Occupancy Rate</p>
                  <p className="text-2xl font-bold mt-1">
                    {residenceStats.capacity
                      ? `${Math.round((residenceStats.occupancy / residenceStats.capacity) * 100)}%`
                      : "0%"}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl p-5 shadow-lg shadow-amber-200 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <ClipboardList className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-sm text-amber-100">Registrations</p>
                  <p className="text-2xl font-bold mt-1">{residenceStats.registrations || 0}</p>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Occupancy vs Capacity Donut */}
                <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl shadow-md border border-indigo-100 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Occupancy Overview</h3>
                  <p className="text-xs text-gray-500 mb-4">Occupied vs available beds</p>
                  <div className="relative" style={{ height: "200px" }}>
                    <Doughnut
                      data={{
                        labels: ["Occupied", "Available"],
                        datasets: [
                          {
                            data: [
                              residenceStats.occupancy || 0,
                              Math.max((residenceStats.capacity || 0) - (residenceStats.occupancy || 0), 0),
                            ],
                            backgroundColor: ["#6366F1", "#C7D2FE"],
                            borderWidth: 0,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: "68%",
                        plugins: { legend: { display: false } },
                      }}
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-2xl font-bold text-indigo-600">
                        {residenceStats.capacity
                          ? `${Math.round((residenceStats.occupancy / residenceStats.capacity) * 100)}%`
                          : "0%"}
                      </p>
                      <p className="text-xs text-gray-500">Filled</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 mt-4 text-xs text-gray-600">
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#6366F1" }} />
                      Occupied &mdash; {residenceStats.occupancy || 0}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#C7D2FE" }} />
                      Available &mdash; {Math.max((residenceStats.capacity || 0) - (residenceStats.occupancy || 0), 0)}
                    </span>
                  </div>
                </div>

                {/* Maintenance Status Bar */}
                <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl shadow-md border border-orange-100 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Maintenance Requests</h3>
                  <p className="text-xs text-gray-500 mb-4">By current status</p>
                  <div className="relative" style={{ height: "200px" }}>
                    <Bar
                      data={{
                        labels: ["Pending", "In Progress", "Completed"],
                        datasets: [
                          {
                            label: "Requests",
                            data: [
                              maintenanceStats.pending || 0,
                              maintenanceStats.inProgress || 0,
                              maintenanceStats.completed || 0,
                            ],
                            backgroundColor: ["#FBBF24", "#60A5FA", "#34D399"],
                            borderRadius: 6,
                            maxBarThickness: 40,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false } },
                        scales: {
                          y: { beginAtZero: true, ticks: { stepSize: 1 }, grid: { color: "rgba(251,191,36,0.1)" } },
                          x: { grid: { display: false } },
                        },
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div className="bg-gray-50 rounded-lg py-2">
                      <p className="text-lg font-bold text-gray-900">{maintenanceStats.total || 0}</p>
                      <p className="text-xs text-gray-500">Total</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg py-2">
                      <p className="text-lg font-bold text-amber-600">{maintenanceStats.pending || 0}</p>
                      <p className="text-xs text-gray-500">Pending</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg py-2">
                      <p className="text-lg font-bold text-emerald-600">{maintenanceStats.completed || 0}</p>
                      <p className="text-xs text-gray-500">Done</p>
                    </div>
                  </div>
                </div>

                {/* Students Room Assignment */}
                <div className="bg-gradient-to-br from-teal-50 to-white rounded-xl shadow-md border border-teal-100 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Room Assignment</h3>
                  <p className="text-xs text-gray-500 mb-4">Students assigned vs not</p>
                  <div className="relative" style={{ height: "200px" }}>
                    <Doughnut
                      data={{
                        labels: ["Assigned", "Not Assigned"],
                        datasets: [
                          {
                            data: [
                              studentsList.filter((s) => s.roomAssigned).length,
                              studentsList.filter((s) => !s.roomAssigned).length,
                            ],
                            backgroundColor: ["#14B8A6", "#FDA4AF"],
                            borderWidth: 0,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: "68%",
                        plugins: { legend: { display: false } },
                      }}
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-2xl font-bold text-teal-600">{studentsList.length}</p>
                      <p className="text-xs text-gray-500">Students</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 mt-4 text-xs text-gray-600">
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#14B8A6" }} />
                      Assigned &mdash; {studentsList.filter((s) => s.roomAssigned).length}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#FDA4AF" }} />
                      Not assigned &mdash; {studentsList.filter((s) => !s.roomAssigned).length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Amenities */}
              {residenceStats.amenities?.length > 0 && (
                <div className="bg-gradient-to-r from-violet-50 via-white to-blue-50 rounded-xl shadow-md border border-violet-100 p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">All Amenities</h3>
                  <div className="flex flex-wrap gap-2">
                    {residenceStats.amenities.map((amenity, index) => (
                      <div key={index} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white text-violet-700 text-sm border border-violet-200 shadow-sm">
                        {getAmenityIcon(amenity)}
                        <span>{amenity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Reports Section */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Reports & Downloads</h3>
                      <p className="text-sm text-gray-500 mt-0.5">Export operational data for reporting and compliance</p>
                    </div>
                    <span className="text-xs text-gray-600 bg-white px-2.5 py-1 rounded-full border border-gray-200 shadow-sm">
                      {reports.length} Reports
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {reports.map((report, index) => {
                      const colors = getColorClasses(report.color);
                      return (
                        <div
                          key={index}
                          className={`group bg-white rounded-lg border-2 border-gray-100 ${colors.border} hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200`}
                        >
                          <div className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className={`p-2 ${colors.bg} rounded-lg`}>
                                <div className={colors.text}>
                                  {report.icon}
                                </div>
                              </div>
                              {report.hasFilter && (
                                <button
                                  onClick={() => setShowBillingFilter(true)}
                                  className="text-gray-400 hover:text-gray-600 transition-colors"
                                  title="Filter report"
                                >
                                  <Filter className="w-4 h-4" />
                                </button>
                              )}
                            </div>

                            <h4 className="font-semibold text-gray-900 text-sm mb-1">{report.title}</h4>
                            <p className="text-xs text-gray-500 leading-relaxed mb-3">{report.desc}</p>

                            <button
                              onClick={report.action}
                              className={`w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md border ${colors.border} ${colors.bg} ${colors.text} hover:shadow-md transition-all duration-200`}
                            >
                              <Download className="w-3.5 h-3.5" />
                              <span>Export Report</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Students Tab */}
          {activeTab === "students" && (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 via-white to-indigo-50">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">Registrations List</h2>
                      <p className="text-xs text-gray-500">{filteredStudents.length} students found</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search students..."
                        value={searchQuery}
                        onChange={handleSearch}
                        className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-gray-900 placeholder-gray-400"
                      />
                    </div>
                    <button onClick={handleFilterLatest} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition">
                      <SortDesc className="w-4 h-4" />
                      Sort by Latest
                    </button>
                    <button onClick={handleDownloadReport} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm transition">
                      <Download className="w-4 h-4" />
                      Download Report
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200">
                    <tr>
                      {["Name", "Email", "Phone", "Student No", "Room Status", "Registered", "Actions"].map((th) => (
                        <th key={th} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{th}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredStudents.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Users className="w-8 h-8 text-gray-400" />
                          </div>
                          <p className="text-gray-500">No students found</p>
                          <p className="text-sm text-gray-400 mt-1">Try adjusting your search</p>
                        </td>
                      </tr>
                    ) : (
                      paginatedStudents.map((student) => (
                        <tr key={student.id} className="hover:bg-blue-50/50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                {(student.name || "?").charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-medium text-gray-900">{student.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{student.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                            {student.phone}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 font-mono">{student.studentNumber}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${student.roomAssigned ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                              {student.roomAssigned ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                              {student.roomAssigned ? "Assigned" : "Not Assigned"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            {new Date(student.registerDate).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => handleViewProfile(student.id)}
                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleAssignClick(student)}
                                className="px-2.5 py-1 text-xs font-medium rounded-md bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition"
                              >
                                Room
                              </button>
                              <button
                                onClick={() => navigate(`/residences/${residenceId}/rooms`, { state: { studentId: student.id, studentFirstName: student.name, studentLastName: student.surname, studentNumber: student.studentNumber, residenceName, residenceId } })}
                                disabled={student.roomAssigned}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md border transition ${student.roomAssigned
                                  ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                                  : "bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                                  }`}
                              >
                                Assign
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
           
            {/* Pagination */}
            {filteredStudents.length > STUDENTS_PER_PAGE && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  Showing {(currentPage - 1) * STUDENTS_PER_PAGE + 1}-
                  {Math.min(currentPage * STUDENTS_PER_PAGE, filteredStudents.length)} of {filteredStudents.length}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
              </div>
          )}

          {/* Maintenance Tab */}
          {activeTab === "maintenance" && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 shadow-lg shadow-purple-200 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-100 font-medium">Total Requests</p>
                      <p className="text-3xl font-bold mt-1">{maintenanceStats.total}</p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Wrench className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-amber-400 to-amber-500 rounded-xl p-5 shadow-lg shadow-amber-200 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-amber-50 font-medium">Pending</p>
                      <p className="text-3xl font-bold mt-1">{maintenanceStats.pending}</p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 shadow-lg shadow-blue-200 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-100 font-medium">In Progress</p>
                      <p className="text-3xl font-bold mt-1">{maintenanceStats.inProgress}</p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Loader className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 shadow-lg shadow-emerald-200 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-100 font-medium">Completed</p>
                      <p className="text-3xl font-bold mt-1">{maintenanceStats.completed}</p>
                    </div>
                    <div className="p-3 bg-white/20 rounded-xl">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 p-5">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  {/* Status Filters */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleStatusFilter("all")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${statusFilter === "all" ? "bg-gray-800 text-white shadow-md" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => handleStatusFilter("Pending")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1 ${statusFilter === "Pending" ? "bg-amber-500 text-white shadow-md" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
                    >
                      <Clock className="w-4 h-4" />
                      Pending
                    </button>
                    <button
                      onClick={() => handleStatusFilter("In Progress")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1 ${statusFilter === "In Progress" ? "bg-blue-500 text-white shadow-md" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}
                    >
                      <Loader className="w-4 h-4" />
                      In Progress
                    </button>
                    <button
                      onClick={() => handleStatusFilter("Completed")}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-1 ${statusFilter === "Completed" ? "bg-emerald-500 text-white shadow-md" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      Completed
                    </button>
                  </div>

                  {/* Priority Filters */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handlePriorityFilter("all")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${priorityFilter === "all" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                    >
                      All Priorities
                    </button>
                    <button
                      onClick={() => handlePriorityFilter("Low")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1 ${priorityFilter === "Low" ? "bg-green-500 text-white" : "bg-green-50 text-green-700 hover:bg-green-100"}`}
                    >
                      <TrendingDown className="w-3 h-3" />
                      Low
                    </button>
                    <button
                      onClick={() => handlePriorityFilter("Medium")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1 ${priorityFilter === "Medium" ? "bg-yellow-500 text-white" : "bg-yellow-50 text-yellow-700 hover:bg-yellow-100"}`}
                    >
                      <AlertTriangle className="w-3 h-3" />
                      Medium
                    </button>
                    <button
                      onClick={() => handlePriorityFilter("High")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1 ${priorityFilter === "High" ? "bg-orange-500 text-white" : "bg-orange-50 text-orange-700 hover:bg-orange-100"}`}
                    >
                      <TrendingUp className="w-3 h-3" />
                      High
                    </button>
                    <button
                      onClick={() => handlePriorityFilter("Emergency")}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1 ${priorityFilter === "Emergency" ? "bg-red-500 text-white" : "bg-red-50 text-red-700 hover:bg-red-100"}`}
                    >
                      <Flame className="w-3 h-3" />
                      Emergency
                    </button>
                  </div>
                </div>

                {/* Search and Sort Bar */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-4 pt-4 border-t border-gray-100">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by student, phone, room, or category..."
                      value={maintenanceSearch}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <button
                    onClick={handleDateSortToggle}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition"
                  >
                    {dateSortOrder === "desc" ? <SortDesc className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
                    {dateSortOrder === "desc" ? "Newest First" : "Oldest First"}
                  </button>
                </div>
              </div>

              {/* Requests List */}
              <div className="space-y-4">
                {filteredMaintenance.length === 0 ? (
                  <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Wrench className="w-10 h-10 text-indigo-400" />
                    </div>
                    <p className="text-gray-600 font-medium text-lg">No maintenance requests found</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your filters or search criteria</p>
                  </div>
                ) : (
                  filteredMaintenance.map((request) => {
                    const priorityBorder = {
                      Low: "border-l-green-400",
                      Medium: "border-l-yellow-400",
                      High: "border-l-orange-400",
                      Emergency: "border-l-red-500",
                    }[request.priority] || "border-l-gray-300";

                    return (
                      <div key={request.id} className={`bg-white rounded-xl shadow-md border border-gray-200 border-l-4 ${priorityBorder} overflow-hidden hover:shadow-lg transition-all duration-200`}>
                        <div className="p-5">
                          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-3 flex-wrap">
                                <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${getStatusColor(request.status)}`}>
                                  {request.status === "Pending" && <Clock className="w-3 h-3" />}
                                  {request.status === "In Progress" && <Loader className="w-3 h-3" />}
                                  {request.status === "Completed" && <CheckCircle className="w-3 h-3" />}
                                  {request.status}
                                </span>
                                <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${getPriorityColor(request.priority)}`}>
                                  {getPriorityIcon(request.priority)}
                                  {request.priority} Priority
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                                  {getCategoryIcon(request.category)}
                                  {request.category}
                                </span>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500">Student</p>
                                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{request.studentName || "Unknown"}</p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500">Phone Number</p>
                                  <p className="text-sm text-gray-700 flex items-center gap-1.5 mt-0.5">
                                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                                    {request.studentPhone || "N/A"}
                                  </p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500">Room Number</p>
                                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{request.roomNumber}</p>
                                </div>
                                <div className="md:col-span-2 lg:col-span-3 bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500">Description</p>
                                  <p className="text-sm text-gray-700 line-clamp-2 mt-0.5">{request.description}</p>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3">
                                  <p className="text-xs text-gray-500">Submitted</p>
                                  <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-0.5">
                                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                    {new Date(request.createdAt).toLocaleString()}
                                  </p>
                                </div>
                                {request.attachments?.length > 0 && (
                                  <div className="bg-gray-50 rounded-lg p-3">
                                    <p className="text-xs text-gray-500">Attachments</p>
                                    <p className="text-sm text-gray-600 mt-0.5">{request.attachments.length} photo(s)</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-row lg:flex-col gap-2">
                              <button
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setAdminNote(request.adminNotes || "");
                                  setShowRequestModal(true);
                                }}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-sm transition"
                              >
                                <Eye className="w-4 h-4" />
                                View & Update
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
          {/* Announcements Tab */}
          {activeTab === "announcements" && (
            <div className="space-y-6">
              {/* Create Announcement */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-violet-50 via-white to-blue-50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-violet-100 rounded-lg">
                      <Megaphone className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">Create New Announcement</h2>
                      <p className="text-xs text-gray-500">Notify your students by email</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={announcement.title}
                      onChange={(e) => setAnnouncement({ ...announcement, title: e.target.value })}
                      placeholder="Announcement title"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-violet-500 focus:ring-2 focus:ring-violet-100 transition-all text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                    <textarea
                      rows={5}
                      value={announcement.content}
                      onChange={(e) => setAnnouncement({ ...announcement, content: e.target.value })}
                      placeholder="Write your announcement here..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-violet-500 focus:ring-2 focus:ring-violet-100 transition-all resize-none text-gray-900 placeholder-gray-400"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Attachments</label>
                      <input
                        type="file"
                        multiple
                        onChange={(e) => setAnnouncement({ ...announcement, attachments: [...e.target.files] })}
                        className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:text-sm file:font-medium file:rounded-lg file:border-0 file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Audience</label>
                      <select
                        value={announcement.audience}
                        onChange={(e) => setAnnouncement({ ...announcement, audience: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-violet-500 focus:ring-2 focus:ring-violet-100 transition-all text-gray-900"
                      >
                        <option value="all" className="text-gray-900">All Students</option>
                        <option value="male" className="text-gray-900">Male Students Only</option>
                        <option value="female" className="text-gray-900">Female Students Only</option>
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={handlePublishAnnouncement}
                    disabled={publishing}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white font-medium shadow-sm transition-all ${publishing
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 hover:shadow-md"
                      }`}
                  >
                    {publishing ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Send className="w-4 h-4" />}
                    {publishing ? "Publishing..." : "Publish Announcement"}
                  </button>
                </div>
              </div>

              {/* Previous Announcements */}
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-slate-50 to-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <ClipboardList className="w-5 h-5 text-indigo-600" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-900">Previous Announcements</h3>
                    </div>
                    <span className="text-xs text-gray-600 bg-white px-2.5 py-1 rounded-full border border-gray-200 shadow-sm">
                      {announcements.length} Total
                    </span>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  {announcements.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gradient-to-br from-violet-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Megaphone className="w-8 h-8 text-violet-400" />
                      </div>
                      <p className="text-gray-600 font-medium">No announcements published yet</p>
                      <p className="text-sm text-gray-400 mt-1">Your published announcements will appear here</p>
                    </div>
                  ) : (
                    announcements.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((a) => {
                      const audienceStyles = {
                        all: "bg-blue-50 text-blue-700 border-blue-200",
                        male: "bg-cyan-50 text-cyan-700 border-cyan-200",
                        female: "bg-pink-50 text-pink-700 border-pink-200",
                      };
                      const audienceLabels = {
                        all: "All Students",
                        male: "Male Only",
                        female: "Female Only",
                      };
                      return (
                        <div key={a.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-violet-200 transition-all duration-200 bg-gradient-to-br from-white to-gray-50/50">
                          <div className="flex justify-between items-start mb-2 gap-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-base font-semibold text-gray-900">{a.title}</h4>
                              {a.audience && (
                                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${audienceStyles[a.audience] || audienceStyles.all}`}>
                                  {audienceLabels[a.audience] || "All Students"}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-gray-400" />
                                {new Date(a.createdAt).toLocaleString()}
                              </span>
                              <button onClick={() => handleDeleteAnnouncement(a.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 rounded-md text-xs font-medium transition">
                                Delete
                              </button>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{a.content}</p>
                          {a.attachments?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <p className="text-xs font-medium text-gray-500 mb-2">Attachments</p>

                              <div className="flex flex-wrap gap-2">
                                {a.attachments.map((file) => (
                                  <a
                                    key={file.id}
                                    href={`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/${file.filePath}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition"
                                  >
                                    <FileText className="w-3.5 h-3.5" />
                                    {file.fileName}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* MSA Support Tab */}
          {activeTab === "msaSupport" && (
            <MSASupportPage
              residenceManagerId={managerData?.residenceManagerId}
              residenceId={residenceId}
              residenceName={residenceName}
            />
          )}

          {/* Manager Tab */}
          {activeTab === "manager" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <Manager residenceId={residenceId} />
            </div>
          )}

          {/* Student Profile Tab */}
          {activeTab === "studentProfile" && selectedStudentId && (
            <ProfileStudentAdm studentId={selectedStudentId} showFinalizeButton={true} />
          )}
        </div>
      </main>

      {/* Request Detail Modal */}
      {showRequestModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="px-5 py-4 flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">Maintenance Request Details</h3>
                  <p className="text-xs text-blue-100">Review and update this request</p>
                </div>
              </div>
              <button onClick={() => setShowRequestModal(false)} className="text-white/70 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto max-h-[calc(90vh-80px)] space-y-4">
              {/* Student Info */}
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  Student Information
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{selectedRequest.studentName || "Unknown"}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-sm text-gray-700 flex items-center gap-1.5 mt-0.5">
                      <Phone className="w-3.5 h-3.5 text-gray-400" />
                      {selectedRequest.studentPhone || "N/A"}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500">Room Number</p>
                    <p className="text-sm font-semibold text-gray-900 mt-0.5">{selectedRequest.roomNumber}</p>
                  </div>
                </div>
              </div>

              {/* Request Info */}
              <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl border border-indigo-100 p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-indigo-600" />
                  Request Details
                </h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500">Category</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5 flex items-center gap-1.5">
                        {getCategoryIcon(selectedRequest.category)}
                        {selectedRequest.category}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Priority</p>
                      <span className={`inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-full font-medium ${getPriorityColor(selectedRequest.priority)}`}>
                        {getPriorityIcon(selectedRequest.priority)}
                        {selectedRequest.priority}
                      </span>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500">Description</p>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap mt-0.5">{selectedRequest.description}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500">Submitted</p>
                    <p className="text-sm text-gray-600 flex items-center gap-1.5 mt-0.5">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      {new Date(selectedRequest.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Attachments */}
              {selectedRequest.attachments && selectedRequest.attachments.length > 0 && (
                <div className="bg-gradient-to-br from-violet-50 to-white rounded-xl border border-violet-100 p-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-violet-600" />
                    Attachments
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedRequest.attachments.map((att) => (
                      <a
                        key={att.id}
                        href={`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/${att.filePath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative group"
                      >
                        <img
                          src={`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/${att.filePath}`}
                          alt="Attachment"
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/100?text=Image+Not+Found";
                          }}
                        />

                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center rounded-lg">
                          <Eye className="w-5 h-5 text-white" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-500" />
                  Admin Notes
                </h4>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all text-gray-900 placeholder-gray-400"
                  placeholder="Add notes about this request (will be visible to student)..."
                />
              </div>

              {/* Status Update */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Update Status</h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => updateRequestStatus(selectedRequest.id, "Pending", adminNote)}
                    disabled={updatingStatus}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${selectedRequest.status === "Pending" ? "bg-amber-500 text-white shadow-md" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
                  >
                    <Clock className="w-4 h-4" />
                    Pending
                  </button>
                  <button
                    onClick={() => updateRequestStatus(selectedRequest.id, "In Progress", adminNote)}
                    disabled={updatingStatus}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${selectedRequest.status === "In Progress" ? "bg-blue-500 text-white shadow-md" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}
                  >
                    <Loader className="w-4 h-4" />
                    In Progress
                  </button>
                  <button
                    onClick={() => updateRequestStatus(selectedRequest.id, "Completed", adminNote)}
                    disabled={updatingStatus}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${selectedRequest.status === "Completed" ? "bg-emerald-500 text-white shadow-md" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"}`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Completed
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Filter Modal */}
      {showBillingFilter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-teal-600 to-blue-600 flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Filter className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">Filter Billing Report</h3>
                <p className="text-xs text-teal-100">Export by date range and status</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Month</label>
                <input
                  type="month"
                  value={billingFilter.startMonth}
                  onChange={(e) => setBillingFilter({ ...billingFilter, startMonth: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Month</label>
                <input
                  type="month"
                  value={billingFilter.endMonth}
                  onChange={(e) => setBillingFilter({ ...billingFilter, endMonth: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                <select
                  value={billingFilter.status}
                  onChange={(e) => setBillingFilter({ ...billingFilter, status: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all text-gray-900"
                >
                  <option value="all" className="text-gray-900">All</option>
                  <option value="Paid" className="text-gray-900">Paid</option>
                  <option value="Partial" className="text-gray-900">Partial</option>
                  <option value="Unpaid" className="text-gray-900">Unpaid</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowBillingFilter(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-medium">Cancel</button>
                <button onClick={downloadFilteredBilling} className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-teal-600 to-blue-600 text-white font-medium hover:from-teal-700 hover:to-blue-700 shadow-sm hover:shadow-md transition-all flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Popup */}
      {showAssignPopup && selectedStudent && (
        <PopupAssign student={selectedStudent} studentId={selectedStudent.id} onClose={() => setShowAssignPopup(false)} />
      )}
    </div>
  );
};

export default ResidenceAdminDashboard;
